(() => {
  const ROOT_SELECTOR = '.product-form[data-template]';
  const LOCAL_KEY = 'lmp-wishlist-products';

  const normalize = (value) =>
    String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();

  const descriptor = (el) => {
    if (!el) return '';
    const cls = typeof el.className === 'string' ? el.className : '';
    return normalize([
      el.textContent || '',
      el.getAttribute && el.getAttribute('aria-label'),
      el.getAttribute && el.getAttribute('title'),
      el.getAttribute && el.getAttribute('data-action'),
      cls
    ].filter(Boolean).join(' '));
  };

  const looksLikeWishlist = (el) =>
    /favori|wishlist|wish-list|favorite/.test(descriptor(el));

  const isActive = (control) => {
    if (!control) return false;
    const value = descriptor(control);
    return (
      control.getAttribute('aria-pressed') === 'true' ||
      control.getAttribute('data-selected') === 'true' ||
      control.classList.contains('active') ||
      control.classList.contains('is-active') ||
      /retirer|remove|supprimer|added|ajoute aux favoris/.test(value)
    );
  };

  const getLocal = () => {
    try {
      const parsed = JSON.parse(localStorage.getItem(LOCAL_KEY) || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  };

  const setLocal = (items) => {
    try { localStorage.setItem(LOCAL_KEY, JSON.stringify(items)); } catch (_) {}
  };

  const setProxyState = (proxy, active) => {
    proxy.setAttribute('aria-pressed', active ? 'true' : 'false');
    proxy.setAttribute(
      'aria-label',
      active ? 'Retirer ce produit des favoris' : 'Ajouter ce produit aux favoris'
    );
  };

  const findNativeWishlistControls = (section, proxy) => {
    const selector = [
      'button',
      'a',
      '[role="button"]',
      'input[type="button"]',
      'input[type="submit"]'
    ].join(',');

    return Array.from(section.querySelectorAll(selector)).filter((el) => {
      if (el === proxy || el.classList.contains('lmp-wishlist-heart')) return false;
      if (el.closest('.lmp-purchase-row')) return false;
      return looksLikeWishlist(el);
    });
  };

  const hideElement = (el) => {
    if (!el || el.classList.contains('lmp-wishlist-heart')) return;
    if (el.dataset && el.dataset.lmpWishlistHidden === 'true') return;

    if (el.dataset) el.dataset.lmpWishlistHidden = 'true';
    el.classList.add('lmp-original-wishlist-hidden');
    el.style.setProperty('display', 'none', 'important');
    el.style.setProperty('margin', '0', 'important');
    el.style.setProperty('padding', '0', 'important');
    el.setAttribute('aria-hidden', 'true');
  };

  const hideWishlistVisuals = (section, proxy) => {
    // 1) Hide known app wrappers/buttons.
    const known = section.querySelectorAll(
      '.Vtl-WishlistButtonWrapper,' +
      '[class*="Vtl-WishlistButtonWrapper"],' +
      '[class*="WishlistButtonWrapper"],' +
      '[class*="wishlist-button-wrapper"],' +
      '[class*="Vtl-WishlistButton"],' +
      '[class*="WishlistButton"],' +
      '[class*="wishlist-button"]'
    );

    known.forEach((el) => {
      if (el === proxy || el.closest('.lmp-purchase-row')) return;
      hideElement(el);
    });

    // 2) Fallback for app markup whose class does not contain "wishlist".
    //    This targets the visible old button by its French label.
    const candidates = section.querySelectorAll('button, a, [role="button"], div, span');
    candidates.forEach((el) => {
      if (el === proxy || el.classList.contains('lmp-wishlist-heart')) return;
      if (el.closest('.lmp-purchase-row')) return;

      const txt = normalize(el.textContent || '');
      if (
        txt !== 'ajouter aux favoris' &&
        txt !== 'retirer des favoris' &&
        txt !== 'retirer aux favoris'
      ) return;

      const wrapper =
        el.closest('.Vtl-WishlistButtonWrapper') ||
        el.closest('[class*="WishlistButtonWrapper"]') ||
        el.closest('[class*="wishlist-button-wrapper"]') ||
        el.closest('[class*="WishlistButton"]') ||
        el.closest('[class*="wishlist-button"]') ||
        el.closest('button, a, [role="button"]') ||
        el.parentElement ||
        el;

      if (wrapper && !wrapper.closest('.lmp-purchase-row')) hideElement(wrapper);
    });
  };

  function setupWishlist(section, proxy, handle) {
    let control = null;
    let controlObserver = null;
    let scanScheduled = false;

    const sync = () => {
      if (control && control.isConnected) {
        setProxyState(proxy, isActive(control));
        return;
      }
      setProxyState(proxy, handle ? getLocal().includes(handle) : false);
    };

    const rescan = () => {
      hideWishlistVisuals(section, proxy);

      const controls = findNativeWishlistControls(section, proxy);
      const found = controls[0] || null;

      if (found !== control) {
        control = found;

        if (controlObserver) {
          controlObserver.disconnect();
          controlObserver = null;
        }

        if (control) {
          controlObserver = new MutationObserver(() => {
            hideWishlistVisuals(section, proxy);
            sync();
          });
          controlObserver.observe(control, {
            attributes: true,
            childList: true,
            subtree: true,
            characterData: true
          });
        }
      }

      controls.forEach(hideElement);
      sync();
      return !!control;
    };

    const scheduleRescan = () => {
      if (scanScheduled) return;
      scanScheduled = true;
      window.requestAnimationFrame(() => {
        scanScheduled = false;
        rescan();
      });
    };

    proxy.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();

      // Find the real app control before hiding its visual wrapper.
      const controlsBeforeHide = findNativeWishlistControls(section, proxy);
      if (controlsBeforeHide[0]) control = controlsBeforeHide[0];

      if (control && control.isConnected) {
        control.click();
        window.setTimeout(scheduleRescan, 60);
        window.setTimeout(scheduleRescan, 250);
        window.setTimeout(scheduleRescan, 700);
        return;
      }

      if (handle) {
        const items = getLocal();
        const index = items.indexOf(handle);
        const adding = index === -1;
        if (adding) items.push(handle);
        else items.splice(index, 1);
        setLocal(items);
        setProxyState(proxy, adding);
      }

      scheduleRescan();
    });

    rescan();

    const sectionObserver = new MutationObserver(scheduleRescan);
    sectionObserver.observe(section, { childList: true, subtree: true });

    window.setTimeout(scheduleRescan, 300);
    window.setTimeout(scheduleRescan, 1000);
    window.setTimeout(scheduleRescan, 2500);
    window.setTimeout(scheduleRescan, 5000);
  }

  function setup(root) {
    if (!root || root.querySelector(':scope > .lmp-purchase-row')) return;

    const quantity = root.querySelector(':scope > .product-quantity');
    const addToCart = root.querySelector(':scope > .product-add-to-cart');
    if (!quantity || !addToCart) return;

    const section = root.closest('.product-section') || root;
    const handle =
      section.getAttribute('data-product-handle') ||
      document.querySelector('.product-section[data-product-handle]')?.getAttribute('data-product-handle') ||
      '';

    const row = document.createElement('div');
    row.className = 'lmp-purchase-row';
    root.insertBefore(row, quantity);
    row.appendChild(quantity);
    row.appendChild(addToCart);

    const wishlist = document.createElement('button');
    wishlist.type = 'button';
    wishlist.className = 'lmp-wishlist-heart';
    wishlist.setAttribute('aria-label', 'Ajouter ce produit aux favoris');
    wishlist.setAttribute('aria-pressed', 'false');
    wishlist.innerHTML =
      '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
      '<path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78Z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    row.appendChild(wishlist);

    setupWishlist(section, wishlist, handle);
  }

  function init() {
    document.querySelectorAll(ROOT_SELECTOR).forEach(setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
